import { AsyncPipe, NgFor, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AsamcoAuthService, Permissions } from '@asamco/base';
import { pathJoin, showErrorSwal } from '@asamco/utils';
import { GroupService, Permission, PermissionService } from '@asamco/user-management/shared';
import { DxButtonModule, DxDataGridModule } from 'devextreme-angular';
import { DxiColumnModule, DxoFilterRowModule, DxoPagerModule, DxoPagingModule, DxoSearchPanelModule, DxoSortingModule } from 'devextreme-angular/ui/nested';
import ODataStore from 'devextreme/data/odata/store';
import { BehaviorSubject, catchError, firstValueFrom, of } from 'rxjs';
import { UserDto } from '../../../services/al-user-v2.service';

type PermissionGridRow = Permission & {
	category: string;
	displayPermissionName: string;
	displayPermissionDescription: string;
	permissionCode: string;
	sourceGroups: string;
	sourceGroupNames: string[];
};

type ViewModel = {
	state: 'loading';
} | {
	state: 'ready';
	userDisplayName: string;
	userSecondaryText: string;
	groups: string[];
	permissions: PermissionGridRow[];
} | {
	state: 'error';
};

const applicationLinkPermissionCodes = new Map<string, string>([
	['839cd3d1-fb13-437a-872b-32c663de2a28', 'AL3_IMPERSONATION'],
	['2bf719b0-712b-41f7-b731-989f35949a31', 'AL3_FUNC_APP'],
	['1f25b2f7-9e3d-4b4a-a3c7-7ecf73ec7e91', 'AL3_TEST_PERMISSION'],
]);

const permissionCodesByKey = new Map<string, string>([
	...Object.entries(Permissions).map(([permissionCode, permissionKey]) => [permissionKey.toLowerCase(), permissionCode] as const),
	...applicationLinkPermissionCodes.entries(),
]);

const permissionDisplayNamesByCode = new Map<string, string>([
	['AL3_ADMINISTRATION', 'Administration Pages'],
	['AL3_USERMANAGEMENT_ASSIGN', 'Assign User Management Permissions'],
	['AL3_USERCOMPANY_VIEW', 'User Companies'],
	['AL3_USERCOMPANY_MANAGE', 'Manage User Companies'],
	['AL3_USER_MANAGE', 'Manage Users'],
	['AL3_COMPANY_REQUESTS_HANDLE', 'Handle Company Requests'],
	['AL3_COMPANY_ACCESS_REQUESTS_HANDLE', 'Company Access Requests'],
	['AL3_COMPANY_VIEW', 'View Companies'],
	['AL3_COMPANY_MANAGE', 'Manage Companies'],
	['AL3_MARKETPLACE', 'Marketplace'],
	['AL3_ADMIN_LINK_OVERVIEW', 'Link Operations'],
	['AL3_MAINTENANCE_VIEW', 'Maintenance Queue Blocker'],
	['AL3_MAINTENANCE_EDIT', 'Edit Maintenance'],
	['AL3_ADMIN_TOOLING', 'Admin Tooling'],
	['AL3_TICKET_INTEGRATION', 'Ticket Integration'],
	['AP_VIEW_APPLICATION_LOGS', 'Application Logs'],
	['AL3_JOBLOG_PATTERN_MATCHER', 'Job Log Pattern Matcher'],
	['AL3_CHANGELOG', 'Changelog'],
	['AL3_APPLICATION_MANAGEMENT', 'Applications'],
	['AL3_LINK_TRANSFER', 'Link Transfer'],
	['AL3_MOCK_DATA', 'Mock Data'],
	['AL3_WARNING_MODAL_MESSAGES', 'Warning Modal Messages'],
	['AL3_FUNC_APP', 'Function App Management'],
	['AL3_INT_TEST', 'Integration Tester'],
	['AS_UM_MI_USER_MANAGEMENT_MENU_ITEM', 'User Management Menu'],
	['AS_UM_MI_USERS', 'Users'],
	['AS_UM_MI_GROUPS', 'Groups'],
]);

@Component({
	selector: 'app-shell-admin-user-permissions-page',
	templateUrl: './admin-user-permissions-page.component.html',
	styleUrls: ['./admin-user-permissions-page.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		AsyncPipe,
		DxButtonModule,
		DxDataGridModule,
		DxiColumnModule,
		DxoFilterRowModule,
		DxoPagerModule,
		DxoPagingModule,
		DxoSearchPanelModule,
		DxoSortingModule,
		NgFor,
		NgIf,
	],
})
export class AdminUserPermissionsPageComponent implements OnInit {
	public readonly viewModel$ = new BehaviorSubject<ViewModel>({
		state: 'loading',
	});

	private readonly userStore: ODataStore;

	constructor(
		private activatedRoute: ActivatedRoute,
		private asamcoAuthService: AsamcoAuthService,
		private groupService: GroupService,
		private permissionService: PermissionService,
		private router: Router,
	) {
		this.userStore = new ODataStore({
			version: 4,
			key: 'id',
			keyType: 'Int64',
			url: pathJoin([this.asamcoAuthService.userSession.backend.odataBaseUrl, 'dynamic/_AL3_vw_Users']),
			beforeSend: this.asamcoAuthService.dxStoreBeforeSend,
			deserializeDates: false,
		});
	}

	ngOnInit(): void {
		void this.load();
	}

	public async back(): Promise<void> {
		await this.router.navigate(['/application-link/admin-tooling-overview/users']);
	}

	public async refresh(): Promise<void> {
		await this.load();
	}

	private async load(): Promise<void> {
		this.viewModel$.next({ state: 'loading' });

		try {
			const userId = Number(this.activatedRoute.snapshot.paramMap.get('id'));
			if (!userId) {
				throw new Error('Invalid user id.');
			}

			const [user, usersPerGroup] = await Promise.all([
				this.loadUser(userId),
				firstValueFrom(this.groupService.getUsersPerGroup('', 0).pipe(catchError(() => of([])))),
			]);

			const userGroups = usersPerGroup.filter(userInGroup => userInGroup.userId === userId);
			const groupNames = Array.from(new Set(userGroups.map(group => group.groupName).filter((groupName): groupName is string => !!groupName)))
				.sort((left, right) => left.localeCompare(right));

			const permissions = await this.loadEffectivePermissions(userGroups);
			this.viewModel$.next({
				state: 'ready',
				userDisplayName: this.getUserDisplayName(user, userId),
				userSecondaryText: this.getUserSecondaryText(user, userId),
				groups: groupNames,
				permissions,
			});
		} catch (error) {
			showErrorSwal(error);
			this.viewModel$.next({ state: 'error' });
		}
	}

	private async loadUser(userId: number): Promise<UserDto | null> {
		try {
			return await this.userStore.byKey(userId) as UserDto;
		} catch {
			return null;
		}
	}

	private async loadEffectivePermissions(userGroups: Array<{ groupId: number; groupName: string }>): Promise<PermissionGridRow[]> {
		const uniqueGroups = Array.from(new Map(userGroups.map(group => [group.groupId, group])).values());
		const permissionEntries = await Promise.all(uniqueGroups.map(async group => ({
			groupName: group.groupName,
			permissions: await firstValueFrom(this.permissionService.getGroupPermissions(group.groupId).pipe(catchError(() => of([])))),
		})));

		const permissionsByKey = new Map<string, PermissionGridRow>();
		for (const entry of permissionEntries) {
			for (const permission of entry.permissions.filter(permission => permission.isAllowed)) {
				const permissionKey = permission.permissionKey.toLowerCase();
				const existingPermission = permissionsByKey.get(permissionKey);

				if (existingPermission) {
					if (entry.groupName && !existingPermission.sourceGroupNames.includes(entry.groupName)) {
						existingPermission.sourceGroupNames.push(entry.groupName);
						existingPermission.sourceGroups = existingPermission.sourceGroupNames.join(', ');
					}
					continue;
				}

				permissionsByKey.set(permissionKey, {
					...permission,
					category: this.permissionCategory(permission),
					displayPermissionName: this.permissionName(permission, permissionKey),
					displayPermissionDescription: this.permissionDescription(permission),
					permissionCode: permissionCodesByKey.get(permissionKey) ?? permission.permissionKey,
					sourceGroupNames: entry.groupName ? [entry.groupName] : [],
					sourceGroups: entry.groupName ?? '',
				});
			}
		}

		return Array.from(permissionsByKey.values())
			.sort((left, right) => {
				const categoryCompare = left.category.localeCompare(right.category);
				if (categoryCompare !== 0) {
					return categoryCompare;
				}

				return left.displayPermissionName.localeCompare(right.displayPermissionName);
			});
	}

	private permissionName(permission: Permission, permissionKey: string): string {
		if (permission.permissionDescription?.trim()) {
			return permission.permissionName;
		}

		const permissionCode = permissionCodesByKey.get(permissionKey) ?? permission.permissionKey;
		return this.readablePermissionName(permissionCode);
	}

	private permissionDescription(permission: Permission): string {
		if (permission.permissionDescription?.trim()) {
			return permission.permissionDescription;
		}

		return permission.permissionName;
	}

	private permissionCategory(permission: Permission): string {
		return [permission.group_1, permission.group_2]
			.filter(group => !!group)
			.join(' / ') || 'Other';
	}

	private readablePermissionName(permissionCode: string): string {
		const displayName = permissionDisplayNamesByCode.get(permissionCode);
		if (displayName) {
			return displayName;
		}

		return permissionCode
			.split('_')
			.filter(part => !['AL3', 'AP', 'AS', 'UM', 'MI'].includes(part))
			.map(part => part.charAt(0) + part.slice(1).toLowerCase())
			.join(' ');
	}

	private getUserDisplayName(user: UserDto | null, userId: number): string {
		const nameParts = [user?.firstName, user?.lastName].filter(part => !!part);
		const fullName = nameParts.join(' ').trim();
		return fullName || user?.userName || user?.username || `User ${userId}`;
	}

	private getUserSecondaryText(user: UserDto | null, userId: number): string {
		return user?.email || user?.userName || user?.username || `User ID ${userId}`;
	}
}
